import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';

import { AdminContext } from '../contexts/admin.context';
import { CreateProjectSchema } from '../schema/project.schema';
import { createModerator, createProject, getModerators } from '../service';
import { getUserId } from '../utils/user';

import Button from '../components/common/Button';
import AdminInput from '../components/common/AdminInput';
import NumberInput from '../components/common/NumberInput';
import { InfoIcon, RocketIcon } from '../components/icons';
import { trackEvent } from '../utils';
import { getTranslation } from '../utils/translation';
import InputSelect from '../components/common/InputSelect';
import { ModeratorForm } from '../components/forms';
import { POPUP_DEFAULT_VALUES } from '../data/default-values';

const ClassProject = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [creatingClass, setCreatingClass] = useState(false);
  const [loadingModerators, setLoadingModerators] = useState(false);
  const [moderators, setModerators] = useState([]);

  const {
    setPopupInfo,
    setContentPopupInfo,
    themeId,
    teamType,
    user,
    setPipVideo,
    portalConfig: {
      productId,
      projectTypes,
      languages = [],
      enableModerators,
      teachersPortalFeatures: { showNumberOfParticipants = true } = {}
    } = {},
    themeConfig: { secondary } = {}
  } = useContext(AdminContext);

  const [languageOptions] = useState([
    { key: '', title: '' },
    ...languages?.map((l) => ({ value: l, label: l.toUpperCase() }))
  ]);
  const [filteredProjectTypes, setFilteredProjectTypes] = useState([]);

  const basePath = `/${themeId}/${teamType}`;

  const bottomDescription = getTranslation(
    t,
    'createProject.bottomDescription',
    false,
    0,
    false,
    true
  );

  const bottomDescription2 = getTranslation(
    t,
    'createProject.bottomDescription2',
    false,
    0,
    false,
    true
  );

  const handleSubmit = async (formValues) => {
    // if (
    //   enableModerators &&
    //   user?.administrator &&
    //   (!formValues?.moderatorEmail || formValues?.moderatorEmail === 'addNewMod')
    // ) {
    //   formik.setFieldError(
    //     'moderatorEmail',
    //     getTranslation(t, 'createProject.errors.moderatorEmailRequired')
    //   );
    //   toast.error(getTranslation(t, 'createProject.errors.moderatorEmailRequired'));
    //   return;
    // }

    const userAccountId = getUserId(user, themeId, teamType, 'account');
    const userId = getUserId(user, themeId, teamType);

    if (!formValues?.projectType) formValues.projectType = filteredProjectTypes?.[0].value;

    const projectTypeObject = filteredProjectTypes?.find((p) => p.value === formValues.projectType);

    const projectValues = {
      accountId: userAccountId,
      productId: projectTypeObject?.customVIPProductId || productId,
      noOfParticipants: !showNumberOfParticipants ? 0 : formValues.noOfParticipants,
      specs: {
        moderator: formValues.moderatorEmail || userId,
        language: formValues.language,
        notTanProtected: false,
        description: formValues.classCodeName,
        projectType: formValues.projectType,
        teamType: teamType?.toLowerCase(),
        teacherRegion: user?.address?.[0]?.street,
        notTanProtected: !showNumberOfParticipants
      }
    };

    try {
      setCreatingClass(true);
      const response = await createProject(projectValues);
      // show succcess popup
      if (response.status === 200) {
        // if (projectTypeObject?.resources?.demoVideo?.url)
        //   setPipVideo(process.env.REACT_APP_AWS_URL + projectTypeObject?.resources?.demoVideo?.url);

        const actionList = [];

        if (projectTypeObject?.resources?.demoVideo?.url?.[i18n.language]) {
          actionList.push({
            _id: 'demoVideo',
            name: getTranslation(t, 'createProject.demoVideo'),
            type: 'outline',
            onClick: () => {
              window.open(
                process.env.REACT_APP_AWS_URL +
                  projectTypeObject?.resources?.demoVideo?.url?.[i18n.language],
                '_blank'
              );
            }
          });
        }

        setPopupInfo({
          isVisible: true,
          icon: RocketIcon,
          iconBg: secondary,
          heading: getTranslation(t, 'createProject.manageYourHead'),
          description: getTranslation(t, 'createProject.yourProjectCreated'),
          // action: {
          //   text: getTranslation(t, 'createProject.manageYourProject'),
          //   type: 'normal',
          //   onClick: () => {
          //     navigate(basePath + '/projects');
          //     setPopupInfo({ isVisible: false });
          //   }
          // },
          actionList: [
            ...actionList,
            {
              _id: 'manageYourProject',
              name: getTranslation(t, 'createProject.manageYourProject'),
              type: 'primary',
              onClick: () => {
                navigate(basePath + '/projects');
                setPopupInfo({ isVisible: false });
              }
            }
          ]
        });
        formik.resetForm();
        return;
      }
      toast.error(getTranslation(t, 'createProject.errors.failedCreateProject'));
    } catch (e) {
      toast.error(getTranslation(t, 'createProject.errors.failedCreateProject'));
      console.error('Filed to create a new project', e);
    } finally {
      formik.resetForm();
      setCreatingClass(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      noOfParticipants: 1,
      classCodeName: '',
      projectType: projectTypes?.find((p) => p.enabled)?.id || projectTypes?.[0]?.id,
      language: '',
      moderatorEmail: getUserId(user, themeId, teamType) || ''
    },
    validationSchema: CreateProjectSchema,
    onSubmit: handleSubmit
  });

  const fetchModerators = async (select = '') => {
    try {
      setLoadingModerators(true);
      const res = await getModerators();
      const addNewModOption = {
        value: 'addNewMod',
        label: getTranslation(t, 'createProject.form.addNewModerator')
      };
      setModerators([
        addNewModOption,
        ...res.data?.map((m) => ({
          value: m.email,
          label: `${m.firstName} ${m.lastName} (${m.email})`
        }))
      ]);

      if (select) {
        const moderator = res.data?.find(
          (m) => m.email?.toLowerCase()?.trim() === select?.toLowerCase()?.trim()
        );
        if (moderator) {
          formik.setFieldValue('moderatorEmail', moderator.email);
        }
      }
    } catch (e) {
      console.error('Filed to get moderators', e);
    } finally {
      setLoadingModerators(false);
    }
  };

  const handleModeratorFormSubmit = async (values) => {
    try {
      await createModerator({ ...values, languageId: user?.languageId });
      fetchModerators(values?.email);
      setContentPopupInfo(POPUP_DEFAULT_VALUES);
    } catch (e) {
      console.error('Filed to create moderator', e);
      const errorMessage =
        e?.response?.data?.message?.detail ||
        getTranslation(t, 'createProject.errors.failedCreateModerator');
      toast.error(errorMessage);
    }
  };

  useEffect(() => {
    if (projectTypes?.length > 0) {
      const filteredProjects = projectTypes
        ?.map(({ id, title, enabled, ...rest }) => ({
          ...rest,
          id,
          value: id,
          label: title?.[i18n.language] || title?.en || title?.de || title?.fr || title?.it,
          disabled: !enabled
        }))
        .filter((p) => !p.disabled);

      setFilteredProjectTypes(filteredProjects);

      if (formik) formik.setFieldValue('projectType', filteredProjects?.[0]?.value);
    }
  }, [projectTypes]);

  useEffect(() => {
    if (enableModerators) {
      fetchModerators();
    }
  }, [enableModerators]);

  useEffect(() => {
    trackEvent('page_view', {
      organization: themeId === 'ch' ? 'SSK' : 'WSF'
    });
  }, []);

  useEffect(() => {
    // Auto-select the only language if there's just one option
    if (languages?.length < 2) {
      formik.setFieldValue('language', languages?.[0]);
    }
  }, [languageOptions, languages]);

  return (
    <div className="overflow-y-auto w-full mini-scrollbar pr-4 pb-20">
      <div className="mb-14">
        <h1
          className="text-2xl font-bold text-left text-cblack-primary mb-2.5"
          dangerouslySetInnerHTML={{ __html: getTranslation(t, 'createProject.listHead') }}
        />
        <div
          className="text=lg text-cblack-primary"
          dangerouslySetInnerHTML={{ __html: getTranslation(t, 'createProject.point1') }}
        />
      </div>

      <div className="mt-5 max-w-xl px-1">
        <form onSubmit={formik.handleSubmit}>
          <InputSelect
            label={getTranslation(t, 'createProject.form.projectType')}
            value={formik.values.projectType}
            options={filteredProjectTypes}
            formik={formik}
            onChange={(value) => {
              formik.setFieldValue('projectType', value);
            }}
            name="projectType"
          />
          {showNumberOfParticipants && (
            <NumberInput
              label={getTranslation(t, 'createProject.form.noOfParticipantsLabel')}
              subtext={getTranslation(t, 'createProject.form.noOfParticipantsSubtext')}
              isDark={true}
              name="noOfParticipants"
              min="0"
              formik={formik}
            />
          )}
          {languages?.length > 1 && (
            <InputSelect
              label={getTranslation(t, 'createProject.form.languageLabel')}
              value={formik.values.language}
              options={languageOptions}
              formik={formik}
              onChange={(value) => {
                formik.setFieldValue('language', value);
              }}
              name="language"
              error={formik.errors.language}
              touched={formik.touched.language}
            />
          )}
          <AdminInput
            label={getTranslation(t, 'createProject.form.classCodeNameLabel')}
            subtext={getTranslation(t, 'createProject.form.classCodeNameSubtext')}
            isDark={true}
            placeholder={getTranslation(t, 'createProject.form.classCodePlaceholder')}
            type="text"
            name="classCodeName"
            formik={formik}
          />
          {enableModerators && user?.administrator && (
            <InputSelect
              loading={loadingModerators}
              options={moderators}
              label={getTranslation(t, 'createProject.form.moderatorEmailLabel')}
              subtext={getTranslation(t, 'createProject.form.moderatorEmailSubtext')}
              isDark={true}
              placeholder={getTranslation(t, 'createProject.form.moderatorEmailPlaceholder')}
              type="text"
              name="moderatorEmail"
              error={formik.errors.moderatorEmail}
              touched={formik.touched.moderatorEmail}
              value={formik.values.moderatorEmail}
              onChange={(value) => {
                if (value === 'addNewMod') {
                  setContentPopupInfo({
                    isVisible: true,
                    maxWidth: 'max-w-3xl',
                    content: (
                      <ModeratorForm
                        setContentPopupInfo={setContentPopupInfo}
                        onSubmit={handleModeratorFormSubmit}
                      />
                    )
                  });
                  return;
                }
                formik.setFieldValue('moderatorEmail', value);
              }}
            />
          )}
          <Button buttonType="login" type="submit" loading={creatingClass} disabled={creatingClass}>
            {getTranslation(t, 'createProject.actionCreate')}
          </Button>
        </form>
      </div>

      {(bottomDescription?.[themeId] || bottomDescription?.default) && (
        <div className="bg-cwhite-secondary text-gray-primary p-4 rounded-lg mt-5 flex gap-2 max-w-xl">
          <div className="mt-1">
            <InfoIcon />
          </div>
          <div
            dangerouslySetInnerHTML={{
              __html: bottomDescription?.[themeId] || bottomDescription?.default
            }}
          />
        </div>
      )}

      {(bottomDescription2?.[themeId] || bottomDescription2?.default) && (
        <div className="mt-5 max-w-xl">
          <div
            dangerouslySetInnerHTML={{
              __html: bottomDescription2?.[themeId] || bottomDescription2?.default
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ClassProject;
